import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { reelsPlanDaysThatFit, type LimitShortfall } from 'fym-shared/src/music24/limitUpsell';
import { TypedMemo } from '../../../../../common/types';
import type { ReelsPlanPreview, ReelsPlanSettings } from '../../../../../../redux/api/fymAPI.types';
import { LimitUpsell } from '../../../../limitUpsell/LimitUpsell';
import { reelsPlanMessages } from './reelsPlanMessages';

interface ReelsPlanUpsellProps {
  preview: ReelsPlanPreview | undefined;
  /** `reelsPlanShortfalls` of the preview. */
  credits: LimitShortfall | null;
  posts: LimitShortfall | null;
  settings: ReelsPlanSettings;
  /** "Plan only what fits": the setup takes the shorter length. */
  onDays: (days: number) => void;
  /** The credit packs (`useVideoCreditGate`), the second way to pay the credits. */
  onBuyCredits: () => void;
}

/**
 * The limit upsell of the Auto campaign setup (design limit-upsell a1, a6), before "Make the plan".
 * The dry run gives the posts of the plan and the posts left this month, the credits and the
 * credits left. A short credit count stops the plan; a short post count does not (the launch
 * schedules what fits), but it shows the same card. "Plan only what fits" is the longest length
 * that fits both.
 */
const ReelsPlanUpsellComponent: React.FC<ReelsPlanUpsellProps> = ({
  preview,
  credits,
  posts,
  settings,
  onDays,
  onBuyCredits,
}) => {
  const intl = useIntl();
  const fitDays = useMemo(() => {
    if (preview === undefined || (credits === null && posts === null) || preview.reelCount <= 0) {
      return null;
    }
    return reelsPlanDaysThatFit({
      days: settings.days,
      cadence: settings.cadence,
      postsPerDay: settings.postsPerDay,
      // Each reel posts one time on each channel of the dry run.
      channels: preview.postCount / preview.reelCount,
      creditsPerReel: preview.credits / preview.reelCount,
      creditsLeft: preview.creditsLeft ?? null,
      postsLeft: typeof preview.scheduledPostsLimit === 'number' ? (preview.scheduledPostsLeft ?? 0) : null,
    });
  }, [credits, posts, preview, settings.cadence, settings.days, settings.postsPerDay]);

  const fit = useMemo(
    () =>
      fitDays === null
        ? null
        : {
            label: intl.formatMessage(reelsPlanMessages.fitDays, { days: fitDays }),
            onClick: () => onDays(fitDays),
          },
    [fitDays, intl, onDays]
  );

  const buyPack = useMemo(
    () => [{ label: intl.formatMessage(reelsPlanMessages.buyPack), onClick: onBuyCredits }],
    [intl, onBuyCredits]
  );

  return (
    <>
      <LimitUpsell shortfall={credits} context="plan" fit={fit} extraActions={buyPack} />
      <LimitUpsell shortfall={posts} context="plan" fit={credits === null ? fit : null} />
    </>
  );
};

export const ReelsPlanUpsell = TypedMemo(ReelsPlanUpsellComponent);
